import CheckboxInput from "components/inputs/checkbox"
import OccupancyAreaTable from "components/property/reports/commercial/occupancy-area-table"
// Data
import areaMeasuresService from 'data/area-measures'
import api_match from 'data/interface/api'
import { parseDate } from "data/lib/dates"
import propertyService from "data/property"
import propertyReportService from "data/reports-property"
import underwritingService from "data/underwriting"
import _ from 'lodash'
import calcService from "data/calc"
import React, { FC, useEffect, useState } from "react"
// Styles
import { ButtonWithPress_s, Label_s } from "styles/app-styles"
// Components
import { InputNumber_s } from "styles/input-styles"
import { DateText_s, ReportForm_s } from "styles/report-styles"

interface OccupancyAreaProps {
  property: object
}

function useForceUpdate(){
  const [value, setValue] = useState(0); // integer state
  return () => setValue(value => value + 1); // update the state to force render
}

const OccupancyArea: FC<OccupancyAreaProps> = (props) => {
  const [start_month, setStartMonth] = useState<string>(underwritingService.getReportBeginDefault(true, null, true))
  const [term, setTerm] = useState<number>(propertyReportService.getReportTermDefault())
  const [areas, setAreas] = useState<object[]>([]);
  const [disabled, setDisabled] = useState<boolean>(false)
  const [include_percentage, setIncludePercentage] = useState<boolean>(true)
  const [data, setData] = useState<object[]>([])
  const forceUpdate = useForceUpdate();

  function submit():void {
    propertyReportService.propertyReport({
      report: 'area_measure',
      params: {
        start_month: underwritingService.toISOdate(start_month),
        term,
        area_measures: areas.map(area => area['areaid']),
        include_percentage
      }
    });
  }

  useEffect(() => {
    const areaSubscription = areaMeasuresService.stream().subscribe(data => {
      api_match(
        data,
        init  => {
          const areas = init.value.filter(area => area['inputtype'] === 'byTenantGroup' || area['inputtype'] === 'byLeaseType')
          setData(areas);
          setAreas(areas);
          if (areas.length === 0) setDisabled(true) 
          else setDisabled(false) 
        },
        ok    => {
          const areas = ok.value.filter(area => area['inputtype'] === 'byTenantGroup' || area['inputtype'] === 'byLeaseType')
          setData(areas);
          setAreas(areas);
          setDisabled(false) 
          if (areas.length === 0) setDisabled(true) 
          else setDisabled(false) 
        },
        err   => {
          setData(err.value)
        },
        fetch => {}
      )
    })
    return function cleanup() {
      areaSubscription.unsubscribe()
    }
  }, [])

  function updateDate(date:string):void {
    const { adjustedDate, validatedTerm } = propertyReportService.updateDate(date, term);
    setStartMonth(adjustedDate);
    if (validatedTerm) setTerm(validatedTerm);
    forceUpdate();
  }

  function updateTerm(term:number):void {
    const adjustedTerm:number|boolean = propertyReportService.validateTerm(term, start_month);
    if (typeof adjustedTerm === 'number'){
      setTerm(adjustedTerm)
    } else {
      setTerm(term);
    }
    forceUpdate();
  }

  useEffect(() => {
    areaMeasuresService.fetchAreaMeasures(propertyService.getStoredPropertyId())
  }, [propertyService.getStoredPropertyId()])

  const runDisabled = disabled || areas.length === 0 || !calcService.calcCheck(props.property)
  const buttonText = disabled ? 'Calculation Required' : 'Run Report'

  return (
    <div>
      <ButtonWithPress_s primary onClick={submit} style={{marginBottom: 20}} disabled={runDisabled}>{buttonText}</ButtonWithPress_s>
      { !disabled && <ReportForm_s>
      <div>
          <Label_s>Start Date</Label_s>
          <DateText_s withoutDays value={parseDate(start_month).format('MM/DD/YYYY')} onChange={updateDate} onBlur={updateDate} persist={updateDate} />
        </div>
        <div>
          <Label_s>Term</Label_s>
          <InputNumber_s value={term} onChange={(e) => updateTerm(e.value)} maxFractionDigits={0} />
        </div>
        <div>
          <Label_s>Display Monthly %</Label_s>
          <CheckboxInput
            field="include_percentage"
            value={include_percentage}
            persist={(field:string, val:boolean) => setIncludePercentage(val)}
          />
        </div>
      </ReportForm_s> }
      { !disabled ? <ReportForm_s>
        <OccupancyAreaTable data={data} selected={areas} setSelected={setAreas} disableRunBtn={setDisabled} />
      </ReportForm_s> : <ReportForm_s>No tenant based area measure records are available to run this report</ReportForm_s> }
    </div>
  )
}

export default OccupancyArea
